import { TrendingDownIcon, TrendingUpIcon, Shield, Clock, Target, Flame, Chrome, Server } from "lucide-react"
import { useDNSQueryStats } from "@/hooks/useDNSAnalytics"
import { Badge } from "@/components/ui/badge"
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"

export function SectionCards() {
  const { data: stats, isLoading } = useDNSQueryStats()

  // Show loading state
  if (isLoading) {
    return (
      <div className="*:data-[slot=card]:shadow-xs grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4 *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card">
        <Card className="@container/card">
          <CardHeader className="relative">
            <CardDescription>Loading...</CardDescription>
            <CardTitle className="@[250px]/card:text-3xl text-2xl font-semibold tabular-nums">
              --
            </CardTitle>
          </CardHeader>
        </Card>
        <Card className="@container/card">
          <CardHeader className="relative">
            <CardDescription>Loading...</CardDescription>
            <CardTitle className="@[250px]/card:text-3xl text-2xl font-semibold tabular-nums">
              --
            </CardTitle>
          </CardHeader>
        </Card>
        <Card className="@container/card">
          <CardHeader className="relative">
            <CardDescription>Loading...</CardDescription>
            <CardTitle className="@[250px]/card:text-3xl text-2xl font-semibold tabular-nums">
              --
            </CardTitle>
          </CardHeader>
        </Card>
        <Card className="@container/card">
          <CardHeader className="relative">
            <CardDescription>Loading...</CardDescription>
            <CardTitle className="@[250px]/card:text-3xl text-2xl font-semibold tabular-nums">
              --
            </CardTitle>
          </CardHeader>
        </Card>
      </div>
    )
  }

  return (
    <div className="*:data-[slot=card]:shadow-xs grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4 *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card">
      <Card className="@container/card">
        <CardHeader className="relative">
          <CardDescription>Threats Blocked Today</CardDescription>
          <CardTitle className="@[250px]/card:text-3xl text-2xl font-semibold tabular-nums">
            {stats?.totalBlocksToday || 0}
          </CardTitle>
          <div className="absolute right-4 top-4">
            <Badge variant="outline" className="flex gap-1 rounded-lg text-xs">
              <Shield className="size-3 text-green-500" />
              Total
            </Badge>
          </div>
        </CardHeader>
        <CardFooter className="flex-col items-start gap-1 text-sm">
          <div className="flex gap-4 text-xs">
            <div className="flex items-center gap-1">
              <Chrome className="size-3 text-blue-500" />
              <span className="text-muted-foreground">Extension:</span>
              <span className="font-medium">{stats?.extensionBlocksToday || 0}</span>
            </div>
            <div className="flex items-center gap-1">
              <Server className="size-3 text-purple-500" />
              <span className="text-muted-foreground">DNS:</span>
              <span className="font-medium">{stats?.dnsBlocksToday || 0}</span>
            </div>
          </div>
          <div className="text-muted-foreground">
            Combined protection sources
          </div>
        </CardFooter>
      </Card>
      <Card className="@container/card">
        <CardHeader className="relative">
          <CardDescription>Weekly Total</CardDescription>
          <CardTitle className="@[250px]/card:text-3xl text-2xl font-semibold tabular-nums">
            {stats?.totalBlocksWeek || 0}
          </CardTitle>
          <div className="absolute right-4 top-4">
            <Badge variant="outline" className="flex gap-1 rounded-lg text-xs">
              <Clock className="size-3 text-blue-500" />
              7 days
            </Badge>
          </div>
        </CardHeader>
        <CardFooter className="flex-col items-start gap-1 text-sm">
          <div className="flex gap-4 text-xs">
            <div className="flex items-center gap-1">
              <Chrome className="size-3 text-blue-500" />
              <span className="text-muted-foreground">Extension:</span>
              <span className="font-medium">{stats?.extensionBlocksWeek || 0}</span>
            </div>
            <div className="flex items-center gap-1">
              <Server className="size-3 text-purple-500" />
              <span className="text-muted-foreground">DNS:</span>
              <span className="font-medium">{stats?.dnsBlocksWeek || 0}</span>
            </div>
          </div>
          <div className="text-muted-foreground">
            Last 7 days activity
          </div>
        </CardFooter>
      </Card>
      <Card className="@container/card">
        <CardHeader className="relative">
          <CardDescription>Active Policies</CardDescription>
          <CardTitle className="@[250px]/card:text-3xl text-2xl font-semibold tabular-nums">
            {stats?.activeRules || 0}
          </CardTitle>
          <div className="absolute right-4 top-4">
            <Badge variant="outline" className="flex gap-1 rounded-lg text-xs">
              <Target className="size-3 text-violet-500" />
            </Badge>
          </div>
        </CardHeader>
        <CardFooter className="flex-col items-start gap-1 text-sm">
          <div className="line-clamp-1 flex gap-2 font-medium text-violet-500">
            Currently active <Target className="size-4 text-violet-500" />
          </div>
          <div className="text-muted-foreground">Protection policies</div>
        </CardFooter>
      </Card>
      <Card className="@container/card">
        <CardHeader className="relative">
          <CardDescription>Protection Streak</CardDescription>
          <CardTitle className="@[250px]/card:text-3xl text-2xl font-semibold tabular-nums">
            {stats?.streakDays || 0}
          </CardTitle>
          <div className="absolute right-4 top-4">
            <Badge variant="outline" className="flex gap-1 rounded-lg text-xs">
              <Flame className="size-3 text-orange-500" />
            </Badge>
          </div>
        </CardHeader>
        <CardFooter className="flex-col items-start gap-1 text-sm">
          <div className="line-clamp-1 flex gap-2 font-medium text-orange-500">
            Consistent protection <Flame className="size-4 text-orange-500" />
          </div>
          <div className="text-muted-foreground">Days in a row</div>
        </CardFooter>
      </Card>
    </div>
  )
}
